import {
  Box,
  Breadcrumbs,
  Card,
  CircularProgress,
  FormLabel,
  Grid,
  Input,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { pageTitle } from '@/utils/pageTitle';
import { CreateEditExpenseModal } from '@/components/Pages/Expenses/CreateEditExpenseModal';
import { DeleteExpenseModal } from '@/components/Pages/Expenses/DeleteExpenseModal';
import { useMemo } from 'react';
import { formatDateUTC } from '@/utils/date';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useExpense } from '@/hooks/useExpenses';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/expenses/$id')({
  component: ExpenseDetailPage,
  head: ({ params }) => pageTitle(`Gastos / ${params.id}`),
});

function ExpenseDetailPage() {
  const { id } = Route.useParams();
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('EXPENSES_READ');
  const canWrite = hasClaim('EXPENSES_WRITE');

  const { data: expense, isLoading } = useExpense(id);

  const showActions = useMemo(() => {
    return expense?.supplierId === undefined && canWrite;
  }, [expense, canWrite]);

  const referenceLink = useMemo(() => {
    if (expense?.supplierId) {
      return (
        <Link to={`/buys/${expense.referenceId}`.toString()}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Ir a compra <OpenInNewIcon />
          </Box>
        </Link>
      );
    }

    return null;
  }, [expense]);

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/expenses' />;
  }

  if (!expense) {
    return <div>Gasto no encontrado</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/expenses'>Gastos</Link>
          <Typography>{expense.id}</Typography>
        </Breadcrumbs>

        {showActions && (
          <Stack direction='row' gap={1}>
            <CreateEditExpenseModal expense={expense} />
            <DeleteExpenseModal id={expense.id.toString()} />
          </Stack>
        )}
      </Stack>

      <Card>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {referenceLink && <Grid xs={12}>{referenceLink}</Grid>}

          <Grid xs={12} md={6}>
            <FormLabel>Fecha</FormLabel>
            <Input
              value={formatDateUTC(expense.date)}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Cuenta</FormLabel>
            <Input
              value={expense.accountName}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Categoría</FormLabel>
            <Input
              value={expense.categoryName || '-'}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Monto</FormLabel>
            <Input
              value={expense.amount.toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                style: 'currency',
                currency: 'ARS',
              })}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12}>
            <FormLabel>Descripción</FormLabel>
            <Textarea
              value={expense.description ?? ''}
              readOnly
              minRows={3}
              maxRows={6}
            />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}
